import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';
import { useQuerySubscription } from 'react-datocms';
import { useRecoilValue } from 'recoil';

import { PageLayout } from '@/components/layouts/page-layout';
import { PageLoading } from '@/components/page-loading';
import { PostDetailView } from '@/components/views/posts/detail';
import { createDatoSubscription } from '@/lib/api/utils/create-dato-subscription';
import { PAGE_REVALIDATE_INTERVAL_WEEK, POST_TYPES, SITE_NAME } from '@/lib/constants';
import { lastVisitedPageState } from '@/state/posts';
import { DatoQueryListenerOptions } from '@/types/cms-types';
import { cmsService } from '@/utils/api/services/cms.service';
import { getPostSchema } from '@/utils/helpers/json-ld';
import { getPostStaticPaths } from '@/utils/posts';

type PageProps = { subscription: DatoQueryListenerOptions };

const Page = ({ subscription }: PageProps): ReactElement => {
  const {
    data: { page },
    status,
    error,
  } = useQuerySubscription(subscription);

  const router = useRouter();
  const lastVisitedPage = useRecoilValue(lastVisitedPageState);

  if (router.isFallback) {
    return <PageLoading />;
  }

  const postJsonLd = getPostSchema(page) || {};
  const backPath = lastVisitedPage > 1 ? `/blog/page/${lastVisitedPage}` : '/blog';

  return (
    <PageLayout
      metaTags={page?.seoMetaTags}
      previewStatus={{ status, error }}
      canonical={page?.canonical}
      headerVariant="light"
      posts={page.posts}
    >
      <Head>
        <title>
          {page.title} - News & Views - {SITE_NAME}
        </title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(postJsonLd)}`,
          }}
        />
      </Head>
      <PostDetailView page={page} backPath={backPath} />
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ params = {}, preview = false }) => {
  const { slug } = params;
  const slugString = slug as string;

  const post = await cmsService.post.getOne(slugString, preview);

  if (!post || !post.slug) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      subscription: await createDatoSubscription({ page: post, slug: slugString, preview, type: 'post' }),
    },
    revalidate: PAGE_REVALIDATE_INTERVAL_WEEK,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { paths } = await getPostStaticPaths({ postType: POST_TYPES.BLOG });
  return {
    paths,
    fallback: 'blocking',
  };
};

export default Page;
